import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: string[];
  showNext: boolean;
}

interface LevelsFile {
  levels: Level[];
}

const LEVELS = (levelsData as LevelsFile).levels;

const PLANE_SRC = 'assets/gcompris-AGPL/planegame/plane.svg';
const CLOUD_SRC = 'assets/gcompris-AGPL/planegame/cloud.svg';
const CLOUD_STORM_SRC = 'assets/gcompris-AGPL/planegame/cloud_storm.svg';

const STAGE_W = 800;
const STAGE_H = 460;
const PLANE_BASE_H = 64;
const CLOUD_BASE_H = 70;
const PLANE_SPEED_PX_PER_S = 480;

interface Cloud {
  el: HTMLDivElement;
  textEl: HTMLDivElement;
  imgEl: HTMLImageElement;
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
  speed: number;
  touched: boolean;
  removeAt: number | null;
  stormUntil: number | null;
}

export function mountPlanegame(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const total = level.data.length;
  // Escala upstream: 15s pra atravessar no Nv1, 10s no último.
  const slowest = 15;
  const fastest = 10;
  const cloudCrossingSec = LEVELS.length > 1
    ? slowest - ((slowest - fastest) * (level.level - 1)) / (LEVELS.length - 1)
    : slowest;
  const cloudSpeedPxPerS = STAGE_W / cloudCrossingSec;
  // Avião encolhe 5% por nível.
  const planeH = PLANE_BASE_H * (1 - 0.05 * (level.level - 1));
  const planeW = planeH * 1.75;
  const cloudH = CLOUD_BASE_H * (1 - 0.05 * (level.level - 1));
  const cloudW = cloudH * 1.6;
  // Nova nuvem a cada metade da travessia (igual upstream).
  const cloudSpawnIntervalMs = cloudCrossingSec * 1000 * 0.5;

  let nextIndex = 0;
  let clouds: Cloud[] = [];
  let cloudCounter = 0;
  let planeX = 16;
  let planeY = (STAGE_H - planeH) / 2;
  let planeAngle = 0;
  let pressed = { up: false, down: false, left: false, right: false };
  let touchTargetX: number | null = null;
  let touchTargetY: number | null = null;
  let lastTs: number | null = null;
  let rafId: number | null = null;
  let spawnTimer: number | null = null;
  let finished = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Caça-números (avião)</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage planegame">
      <div class="feedback" aria-live="polite"></div>
      <p class="planegame__hint">${level.objective}</p>
      <div class="planegame__stage" tabindex="0" aria-label="Área do jogo">
        <div class="planegame__sky"></div>
        <img class="planegame__plane" src="${PLANE_SRC}" alt="Avião" draggable="false" />
        <div class="planegame__hud" aria-live="polite">
          ${level.showNext ? `Próximo: <strong class="planegame__next-num">${level.data[0]}</strong>` : 'Pegue os números em ordem'}
        </div>
        <div class="planegame__progress" aria-live="polite">0 / ${total}</div>
      </div>
      <p class="planegame__controls">Setas do teclado ou toque na tela pra mover o avião</p>
    </section>
  `;
  ctx.root.appendChild(page);

  const stage = page.querySelector<HTMLDivElement>('.planegame__stage')!;
  const sky = page.querySelector<HTMLDivElement>('.planegame__sky')!;
  const planeEl = page.querySelector<HTMLImageElement>('.planegame__plane')!;
  const hudEl = page.querySelector<HTMLDivElement>('.planegame__hud')!;
  const progressEl = page.querySelector<HTMLDivElement>('.planegame__progress')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  stage.style.setProperty('--stage-w', `${STAGE_W}px`);
  stage.style.setProperty('--stage-h', `${STAGE_H}px`);
  planeEl.style.width = `${planeW}px`;
  planeEl.style.height = `${planeH}px`;

  function applyPlaneTransform(): void {
    planeEl.style.transform = `translate(${planeX}px, ${planeY}px) rotate(${planeAngle}deg)`;
  }
  applyPlaneTransform();

  const onKeyDown = (e: KeyboardEvent): void => {
    if (finished) return;
    let handled = true;
    if (e.key === 'ArrowUp') pressed.up = true;
    else if (e.key === 'ArrowDown') pressed.down = true;
    else if (e.key === 'ArrowLeft') pressed.left = true;
    else if (e.key === 'ArrowRight') pressed.right = true;
    else handled = false;
    if (handled) {
      touchTargetX = null;
      touchTargetY = null;
      e.preventDefault();
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowUp') pressed.up = false;
    else if (e.key === 'ArrowDown') pressed.down = false;
    else if (e.key === 'ArrowLeft') pressed.left = false;
    else if (e.key === 'ArrowRight') pressed.right = false;
  };
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  function stageToLocal(clientX: number, clientY: number): [number, number] {
    const rect = stage.getBoundingClientRect();
    const sx = STAGE_W / rect.width;
    const sy = STAGE_H / rect.height;
    return [(clientX - rect.left) * sx, (clientY - rect.top) * sy];
  }

  const onPointer = (e: PointerEvent): void => {
    if (finished) return;
    const [lx, ly] = stageToLocal(e.clientX, e.clientY);
    touchTargetX = lx - planeW / 2;
    touchTargetY = ly - planeH / 2;
    pressed = { up: false, down: false, left: false, right: false };
    e.preventDefault();
  };
  stage.addEventListener('pointerdown', onPointer);
  stage.addEventListener('pointermove', (e) => {
    if (e.buttons === 1 || e.pointerType === 'touch') onPointer(e);
  });

  function spawnCloud(): void {
    if (finished) return;
    cloudCounter++;
    // 1 em cada 3 (ou random 1/2) carrega o número-alvo (igual upstream).
    let text: string;
    if (cloudCounter % 3 === 0 || Math.random() < 0.5) {
      text = level.data[nextIndex]!;
      cloudCounter = 0;
    } else {
      const min = Math.max(0, nextIndex - 1);
      const range = total - 1 - min;
      const offset = Math.floor(Math.random() * Math.max(1, Math.min(nextIndex + 3, range + 1)));
      const idx = Math.min(min + offset, total - 1);
      text = level.data[idx]!;
    }
    const cloud = document.createElement('div');
    cloud.className = 'planegame__cloud';
    cloud.style.width = `${cloudW}px`;
    cloud.style.height = `${cloudH}px`;
    const img = document.createElement('img');
    img.src = CLOUD_SRC;
    img.alt = '';
    img.draggable = false;
    img.className = 'planegame__cloud-img';
    const txt = document.createElement('div');
    txt.className = 'planegame__cloud-text';
    txt.textContent = text;
    cloud.appendChild(img);
    cloud.appendChild(txt);
    sky.appendChild(cloud);
    const y = Math.floor(Math.random() * (STAGE_H - cloudH));
    const c: Cloud = {
      el: cloud,
      textEl: txt,
      imgEl: img,
      text,
      x: STAGE_W,
      y,
      w: cloudW,
      h: cloudH,
      speed: cloudSpeedPxPerS,
      touched: false,
      removeAt: null,
      stormUntil: null,
    };
    cloud.style.transform = `translate(${c.x}px, ${c.y}px)`;
    clouds.push(c);
  }

  function overlaps(a: Cloud, px: number, py: number, pw: number, ph: number): boolean {
    return !(a.x + a.w < px || a.x > px + pw || a.y + a.h < py || a.y > py + ph);
  }

  function tick(ts: number): void {
    if (finished) return;
    if (lastTs === null) lastTs = ts;
    const dt = Math.min(0.05, (ts - lastTs) / 1000);
    lastTs = ts;

    // Movimento do avião (teclado).
    let vx = 0;
    let vy = 0;
    if (pressed.right) vx += 1;
    if (pressed.left) vx -= 1;
    if (pressed.down) vy += 1;
    if (pressed.up) vy -= 1;
    if (vx !== 0 || vy !== 0) {
      const len = Math.hypot(vx, vy) || 1;
      planeX += (vx / len) * PLANE_SPEED_PX_PER_S * dt;
      planeY += (vy / len) * PLANE_SPEED_PX_PER_S * dt;
      planeAngle = vx * 10;
    } else if (touchTargetX !== null && touchTargetY !== null) {
      const dx = touchTargetX - planeX;
      const dy = touchTargetY - planeY;
      const dist = Math.hypot(dx, dy);
      const step = PLANE_SPEED_PX_PER_S * dt;
      if (dist <= step) {
        planeX = touchTargetX;
        planeY = touchTargetY;
        touchTargetX = null;
        touchTargetY = null;
        planeAngle = 0;
      } else {
        planeX += (dx / dist) * step;
        planeY += (dy / dist) * step;
        planeAngle = Math.max(-12, Math.min(12, (dx / dist) * 12));
      }
    } else if (planeAngle !== 0) {
      planeAngle *= 0.85;
      if (Math.abs(planeAngle) < 0.2) planeAngle = 0;
    }
    planeX = Math.max(0, Math.min(STAGE_W - planeW, planeX));
    planeY = Math.max(0, Math.min(STAGE_H - planeH, planeY));
    applyPlaneTransform();

    // Movimento + colisão das nuvens.
    const now = ts;
    for (let i = clouds.length - 1; i >= 0; i--) {
      const c = clouds[i]!;
      if (c.removeAt !== null) {
        if (now >= c.removeAt) {
          c.el.remove();
          clouds.splice(i, 1);
        }
        continue;
      }
      c.x -= c.speed * dt;
      // Tira a tempestade após 2s.
      if (c.stormUntil !== null && now >= c.stormUntil) {
        c.stormUntil = null;
        c.touched = false;
        c.imgEl.src = CLOUD_SRC;
        c.el.classList.remove('planegame__cloud--storm');
      }
      c.el.style.transform = `translate(${c.x}px, ${c.y}px)`;
      if (c.x + c.w < 0) {
        c.el.remove();
        clouds.splice(i, 1);
        continue;
      }
      if (overlaps(c, planeX, planeY, planeW, planeH)) {
        if (c.text === level.data[nextIndex]) {
          handleCorrect(c);
        } else if (!c.touched) {
          handleWrong(c);
        }
      }
    }
  }

  function handleCorrect(c: Cloud): void {
    c.touched = true;
    c.removeAt = performance.now() + 360;
    c.el.classList.add('planegame__cloud--ok');
    ctx.audio.play('correct');
    showFeedback('ok');
    nextIndex++;
    if (level.showNext) {
      if (nextIndex < total) {
        const nextNum = page.querySelector<HTMLElement>('.planegame__next-num');
        if (nextNum) nextNum.textContent = level.data[nextIndex]!;
      } else {
        hudEl.textContent = 'Completo!';
      }
    }
    progressEl.textContent = `${nextIndex} / ${total}`;
    if (nextIndex >= total) finishLevel();
  }

  function handleWrong(c: Cloud): void {
    c.touched = true;
    c.stormUntil = performance.now() + 2000;
    c.imgEl.src = CLOUD_STORM_SRC;
    c.el.classList.add('planegame__cloud--storm');
    ctx.audio.play('wrong');
    showFeedback('err');
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function loop(ts: number): void {
    tick(ts);
    rafId = window.requestAnimationFrame(loop);
  }

  function finishLevel(): void {
    finished = true;
    if (rafId !== null) window.cancelAnimationFrame(rafId);
    rafId = null;
    if (spawnTimer !== null) window.clearInterval(spawnTimer);
    spawnTimer = null;
    for (const c of clouds) c.el.remove();
    clouds = [];
    ctx.audio.play('win');
    ctx.onComplete(level.level, total);
    const sect = page.querySelector<HTMLElement>('.activity-stage')!;
    sect.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você pegou todos os ${total} números em ordem.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    sect.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = sect.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/planegame/${level.level + 1}`;
      });
    }
  }

  // Boot.
  spawnCloud();
  spawnTimer = window.setInterval(spawnCloud, cloudSpawnIntervalMs);
  rafId = window.requestAnimationFrame(loop);
  stage.focus();

  return {
    unmount(): void {
      finished = true;
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      if (spawnTimer !== null) window.clearInterval(spawnTimer);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      ctx.root.innerHTML = '';
    },
  };
}
